import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ClipboardCheck, Trash2 } from 'lucide-react';
import Navigation from '@/components/Navigation';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MyBets = () => {
  const [bets, setBets] = useState([]);
  const [record, setRecord] = useState({ wins: 0, losses: 0, pending: 0 });
  const [loading, setLoading] = useState(true);

  const fetchBets = async () => {
    try {
      const res = await axios.get(`${API}/bets`, { withCredentials: true });
      setBets(res.data.bets || []);
      setRecord(res.data.record || {});
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchBets(); }, []);

  const setResult = async (id, result) => {
    await axios.patch(`${API}/bets/${id}`, { result }, { withCredentials: true });
    fetchBets();
  };

  const remove = async (id) => {
    await axios.delete(`${API}/bets/${id}`, { withCredentials: true });
    toast.success('Slip deleted');
    fetchBets();
  };

  const byDate = bets.reduce((acc, b) => { (acc[b.date] = acc[b.date] || []).push(b); return acc; }, {});
  const resColor = (r) => r === 'won' ? 'border-brand-lime text-brand-lime' : r === 'lost' ? 'border-red-400 text-red-400' : 'border-yellow-400 text-yellow-400';

  return (
    <div className="min-h-screen bg-[#0a1628]">
      <Navigation />
      <main className="pt-20 pb-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white uppercase tracking-tight flex items-center gap-3 mb-2">
            <ClipboardCheck className="h-8 w-8 text-brand-lime" />
            My Bets
          </h1>
          <p className="text-muted-foreground mb-6">Saved slips and your win/loss record over time</p>

          <div className="grid grid-cols-3 gap-3 mb-8">
            <div className="bg-[#0f1e36] border border-[#1e3a5f] rounded p-4 text-center">
              <div className="text-[10px] text-muted-foreground uppercase">Wins</div>
              <div className="font-heading text-3xl font-bold text-brand-lime" data-testid="bets-wins">{record.wins}</div>
            </div>
            <div className="bg-[#0f1e36] border border-[#1e3a5f] rounded p-4 text-center">
              <div className="text-[10px] text-muted-foreground uppercase">Losses</div>
              <div className="font-heading text-3xl font-bold text-red-400" data-testid="bets-losses">{record.losses}</div>
            </div>
            <div className="bg-[#0f1e36] border border-[#1e3a5f] rounded p-4 text-center">
              <div className="text-[10px] text-muted-foreground uppercase">Pending</div>
              <div className="font-heading text-3xl font-bold text-yellow-400">{record.pending}</div>
            </div>
          </div>

          {loading ? (
            <div className="space-y-3">{[1, 2, 3].map(i => <div key={i} className="h-24 skeleton rounded-lg" />)}</div>
          ) : bets.length === 0 ? (
            <Card className="bg-[#0f1e36] border-[#1e3a5f]"><CardContent className="p-12 text-center text-muted-foreground">No saved slips yet — hit "Save Slip" on Props Picks or Sports Bot.</CardContent></Card>
          ) : (
            Object.entries(byDate).map(([date, list]) => (
              <div key={date} className="mb-6">
                <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">{date}</div>
                <div className="space-y-3">
                  {list.map(b => (
                    <Card key={b.id} className="bg-[#0f1e36] border-[#1e3a5f]" data-testid={`bet-${b.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-white font-semibold">{b.title}</span>
                            {b.combined_odds && <span className="font-mono text-brand-cyan text-sm">{b.combined_odds}</span>}
                            <Badge variant="outline" className={resColor(b.result)}>{b.result.toUpperCase()}</Badge>
                          </div>
                          <div className="flex gap-1.5">
                            <Button size="sm" variant="outline" onClick={() => setResult(b.id, 'won')} className="border-brand-lime/40 text-brand-lime hover:bg-brand-lime/10 h-7 px-2 text-xs" data-testid={`bet-won-${b.id}`}>Won</Button>
                            <Button size="sm" variant="outline" onClick={() => setResult(b.id, 'lost')} className="border-red-400/40 text-red-400 hover:bg-red-400/10 h-7 px-2 text-xs">Lost</Button>
                            <Button size="sm" variant="outline" onClick={() => setResult(b.id, 'pending')} className="border-[#1e3a5f] text-muted-foreground hover:bg-[#112240] h-7 px-2 text-xs">Pending</Button>
                            <Button size="sm" variant="ghost" onClick={() => remove(b.id)} className="text-muted-foreground hover:text-red-400 h-7 px-2"><Trash2 className="h-3.5 w-3.5" /></Button>
                          </div>
                        </div>
                        <div className="text-xs text-muted-foreground space-y-0.5">
                          {(b.legs || []).map((l, i) => (
                            <div key={i}>{i + 1}. <span className="text-white">{l.player}</span> — {l.prop} <span className="font-mono text-brand-lime">{l.odds}</span></div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </main>
    </div>
  );
};

export default MyBets;
